import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Box, RadioGroup, FormControlLabel, Radio,
  TextField, Tooltip, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, CircularProgress
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { QRCode } from 'react-qrcode-logo';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { getLocale } from '../utils/dateLocale';
import { getErrorMessage } from '../utils/errorMessage';
import api from '../utils/apiClient';
import { useNotifications } from '../context/NotificationContext';
import { API_BASE as API_BASE_URL } from '../utils/apiConfig';


const PaymentDialog = ({ open, onClose, billId, onPaymentSuccess, showAttempts = false }) => {
  const { t } = useTranslation('billing');
  const { showError } = useNotifications();
  const theme = useTheme();

  const [billData, setBillData] = useState(null);
  const [loadingBill, setLoadingBill] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  // Курс USDT→RUB — только с сервера, без значения «по умолчанию» (как в pages/Bill.js)
  const [exchangeRate, setExchangeRate] = useState(null);
  const rateKnown = Number(exchangeRate) > 0 || String(billData?.currency || '').toUpperCase() === 'USD';
  const [expiresAt, setExpiresAt] = useState(null);
  const [remainingMs, setRemainingMs] = useState(0);
  const [attempts, setAttempts] = useState([]);
  const [creating, setCreating] = useState(false);
  const [usdtCreated, setUsdtCreated] = useState(false);
  const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Загрузка счёта по ID
  useEffect(() => {
    if (!open || !billId) return;
    setPaymentMethod('');
    setExpiresAt(null);
    setRemainingMs(0);
    setUsdtCreated(false);
    loadBill();
  }, [open, billId]);

  const loadBill = async () => {
    try {
      setLoadingBill(true);
      const res = await api.get(`${API_BASE_URL}bill/${billId}`);
      setBillData(res.data);
      if (res.data?.payments) {
        setAttempts(res.data.payments.map(formatAttempt));
      }
    } catch (e) {
      console.error('PaymentDialog: ошибка загрузки счёта', e);
    } finally {
      setLoadingBill(false);
    }
  };

  const parseAmount = (val) => {
    if (!val) return 0;
    const s = String(val).replace(/[^0-9.,]/g, '').replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
  };

  const formatAttempt = (a) => {
    const iso = a?.date_create || a?.created_at || new Date().toISOString();
    const method = String(a?.method || '');
    const amountRub = Number(a?.amount_rub || 0);
    const amountUsdt = Number(a?.amount_usdt || 0);
    const isUsdt = method.toLowerCase() === 'usdt' || method.toLowerCase() === 'crypto';
    return {
      id: a?.id,
      time: new Date(iso).toLocaleString(getLocale()),
      createdAtIso: iso,
      method: isUsdt ? 'USDT (TRC-20)' : t(`method${method.charAt(0).toUpperCase() + method.slice(1).toLowerCase()}`, { defaultValue: method }),
      rawMethod: method,
      amount: amountUsdt > 0 && isUsdt ? `${amountUsdt} USDT` : (amountRub > 0 ? `${amountRub.toLocaleString(getLocale())} р` : '-'),
      status: toStatusLabel(a?.status),
      rawStatus: String(a?.status || ''),
      course: Number(a?.course || 0),
    };
  };

  const toStatusLabel = (s) => {
    const v = String(s || '').toLowerCase();
    if (v === 'pending') return t('statusPending');
    if (v === 'paid' || v === 'success' || v === 'completed') return t('statusPaid');
    if (v === 'waiting') return t('statusWaiting');
    if (v.includes('check')) return t('statusChecking');
    if (v === 'create' || v === 'start') return t('statusCreated');
    return s || '-';
  };

  const getStatusColor = (s) => {
    const v = String(s || '').toLowerCase();
    if (v === 'paid' || v === 'success') return 'success';
    if (v === 'pending' || v === 'waiting') return 'info';
    if (v === 'create') return 'default';
    return 'default';
  };

  const fetchCourse = async () => {
    try {
      const res = await api.get(`${API_BASE_URL}course`);
      const raw = res?.data?.course;
      const value = Number(
        (raw && (raw.course ?? raw.value)) ?? res?.data?.course ?? res?.data?.rate ?? res?.data?.rub_per_usdt ?? 0
      );
      if (Number.isFinite(value) && value > 0) {
        setExchangeRate(value);
        return value;
      }
      return exchangeRate;
    } catch (e) {
      return exchangeRate;
    }
  };

  const computedUsdtAmount = useMemo(() => {
    if (!billData) return '0.00';
    // Долларовый счёт — один к одному; рублёвый — по курсу
    if (String(billData.currency || '').toUpperCase() === 'USD') return Number(billData.amount || 0).toFixed(2);
    const rub = parseAmount(billData.amount);
    const rate = Number(exchangeRate) || 0;
    if (rate <= 0) return '0.00';
    return (rub / rate).toFixed(2);
  }, [billData, exchangeRate]);

  // Кошелёк исполнителя счёта отдаёт сервер; без него оплата в USDT не предлагается
  const walletAddress = billData?.usdt_wallet || '';

  const createPaymentRecord = async ({ method, status, overrideRate }) => {
    if (!billData?.id) return null;
    try {
      setCreating(true);
      const isUsd = String(billData.currency || '').toUpperCase() === 'USD';
      const rubAmount = isUsd ? null : parseAmount(billData.amount);
      const rate = (overrideRate != null ? Number(overrideRate) : Number(exchangeRate)) || 0;
      if (String(method).toUpperCase() === 'USDT' && !isUsd && rate <= 0) {
        showError(t('courseUnavailable'));
        return null;
      }
      const usdtAmount = isUsd ? Number(Number(billData.amount || 0).toFixed(2)) : (rate > 0 ? Number((rubAmount / rate).toFixed(2)) : 0);
      const payload = {
        bill_id: billData.id,
        method,
        amount_rub: rubAmount,
        amount_usdt: method === 'USDT' ? usdtAmount : 0,
        course: method === 'USDT' ? rate : 0,
        status,
      };
      const res = await api.post(`${API_BASE_URL}bill_payment`, payload);
      const raw = res?.data?.payment ?? res?.data ?? payload;
      const created = {
        id: raw?.id,
        bill_id: raw?.bill_id ?? billData.id,
        method: raw?.method ?? method,
        amount_rub: raw?.amount_rub ?? payload.amount_rub,
        amount_usdt: raw?.amount_usdt ?? payload.amount_usdt,
        course: raw?.course ?? payload.course,
        status: raw?.status ?? status,
        date_create: raw?.date_create ?? new Date().toISOString(),
      };
      const ui = formatAttempt(created);
      setAttempts((prev) => {
        const filtered = prev.filter((a) => !(
          String(a?.rawStatus || '').toLowerCase() === 'create' &&
          String(a?.rawMethod || '').toLowerCase() === String(created.method || '').toLowerCase()
        ));
        return [ui, ...filtered];
      });
      return created;
    } catch (err) {
      console.error('PaymentDialog: ошибка создания платежа', err);
      return null;
    } finally {
      setCreating(false);
    }
  };

  const updatePaymentStatus = async (paymentId, status) => {
    if (!paymentId) return false;
    try {
      setCreating(true);
      await api.put(`${API_BASE_URL}bill_payment/${paymentId}`, { status });
      setAttempts((prev) => prev.map((a) =>
        a.id === paymentId ? { ...a, rawStatus: status, status: toStatusLabel(status) } : a
      ));
      return true;
    } catch (e) {
      console.error('PaymentDialog: ошибка обновления статуса', e);
      return false;
    } finally {
      setCreating(false);
    }
  };

  // USDT flow
  useEffect(() => {
    if (!open || paymentMethod !== 'usdt') {
      if (paymentMethod !== 'usdt') {
        setExpiresAt(null);
        setRemainingMs(0);
        setUsdtCreated(false);
      }
      return;
    }
    const existingCreate = attempts.find((a) =>
      String(a?.rawStatus || '').toLowerCase() === 'create' &&
      String(a?.rawMethod || '').toLowerCase() === 'usdt'
    );
    if (existingCreate?.createdAtIso) {
      const createdAt = new Date(existingCreate.createdAtIso);
      const expiry = new Date(createdAt.getTime() + 30 * 60 * 1000);
      const remaining = Math.max(0, expiry - new Date());
      setExpiresAt(expiry);
      setRemainingMs(remaining);
      if (existingCreate.course > 0) setExchangeRate(existingCreate.course);
      setUsdtCreated(true);
      return;
    }
    (async () => {
      const rate = await fetchCourse();
      const expiry = new Date(Date.now() + 30 * 60 * 1000);
      setExpiresAt(expiry);
      setRemainingMs(expiry - new Date());
      if (!usdtCreated) {
        await createPaymentRecord({ method: 'USDT', status: 'create', overrideRate: rate });
        setUsdtCreated(true);
      }
    })();
  }, [paymentMethod]);

  // Таймер
  useEffect(() => {
    if (!expiresAt) return;
    const id = setInterval(() => {
      setRemainingMs(Math.max(0, expiresAt - new Date()));
    }, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const remainingTime = useMemo(() => {
    const total = Math.max(0, remainingMs);
    const mm = String(Math.floor(total / 60000)).padStart(2, '0');
    const ss = String(Math.floor((total % 60000) / 1000)).padStart(2, '0');
    return `${mm}:${ss}`;
  }, [remainingMs]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(walletAddress);
      setCopyTooltipOpen(true);
      setTimeout(() => setCopyTooltipOpen(false), 1200);
    } catch (_) {}
  };

  const handleDownloadPdf = async () => {
    if (!billData?.id) return;
    try {
      setDownloadingPdf(true);
      const res = await api.get(`${API_BASE_URL}bill/${billData.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bill_${billData.id.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      if (e?.response?.status === 412) {
        showError(t('requisitesMissing'));
      } else {
        console.error('PaymentDialog: ошибка скачивания PDF', e);
      }
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!billData?.id || !paymentMethod) return;

    if (paymentMethod === 'usdt') {
      let existing = attempts.find((a) =>
        String(a?.rawStatus || '').toLowerCase() === 'create' &&
        String(a?.rawMethod || '').toLowerCase() === 'usdt'
      );
      if (!existing) {
        const created = await createPaymentRecord({ method: 'USDT', status: 'create' });
        if (created?.id) existing = { id: created.id };
      }
      if (existing?.id) {
        const ok = await updatePaymentStatus(existing.id, 'pending');
        if (ok) {
          onPaymentSuccess && onPaymentSuccess('waiting');
          onClose();
        }
      }
      return;
    }

    if (paymentMethod === 'card') {
      try {
        setCreating(true);
        const res = await api.post(`${API_BASE_URL}yukassa/create`, { bill_id: billData.id });
        if (res?.data?.confirmation_url) {
          window.location.href = res.data.confirmation_url;
        }
      } catch (e) {
        alert(getErrorMessage(e, t('paymentMethodUnavailable')));
      } finally {
        setCreating(false);
      }
      return;
    }

    const method = paymentMethod === 'bank_transfer' ? 'bank_transfer' : 'crypto';
    const created = await createPaymentRecord({ method, status: 'pending' });
    if (created) {
      onPaymentSuccess && onPaymentSuccess('waiting');
      onClose();
    }
  };

  if (!billId) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('payment')}</DialogTitle>
      <DialogContent dividers>
        {loadingBill ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !billData ? (
          <Typography color="error">Не удалось загрузить счёт</Typography>
        ) : (
          <>
            <Typography sx={{ mb: 2 }}>{t('choosePaymentMethod')}</Typography>
            <RadioGroup value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} sx={{ mb: 2 }}>
              {billData?.usdt_available && <FormControlLabel value="usdt" control={<Radio />} label={t('paymentUsdt')} />}
              <FormControlLabel value="card" control={<Radio />} label={t('paymentCard')} />
              <FormControlLabel value="bank_transfer" control={<Radio />} label={t('paymentBankTransfer')} />
              <FormControlLabel value="crypto" control={<Radio />} label={t('paymentCryptoOther')} />
            </RadioGroup>

            {paymentMethod === 'card' && (
              <Typography color="text.secondary">{t('cardPaymentDescription')}</Typography>
            )}

            {paymentMethod === 'bank_transfer' && (
              <Box>
                <Typography sx={{ mb: 1 }}>{t('bankTransferDescription')}</Typography>
                <Button variant="outlined" startIcon={<DownloadIcon />} disabled={downloadingPdf} onClick={handleDownloadPdf}>
                  {t('downloadInvoicePdf')}
                </Button>
              </Box>
            )}

            {paymentMethod === 'crypto' && (
              <Typography color="text.secondary">{t('cryptoDescription')}</Typography>
            )}

            {paymentMethod === 'usdt' && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', rowGap: 2 }}>
                <Typography color="error" variant="body2">{t('usdtWarning')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField label={t('labelWallet')} value={walletAddress} InputProps={{ readOnly: true }} fullWidth size="small" />
                  <Tooltip open={copyTooltipOpen} title={t('copied')} disableHoverListener>
                    <Button variant="outlined" onClick={copyToClipboard}>{t('copyButton')}</Button>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ border: theme.palette.mode === 'light' ? '1px solid #eee' : 'none' }}>
                    <QRCode
                      value={`trc20:${walletAddress}`}
                      size={180}
                      quietZone={8}
                      fgColor={theme.palette.mode === 'dark' ? '#fff' : '#000'}
                      bgColor="transparent"
                      eyeRadius={4}
                      ecLevel="M"
                    />
                  </Box>
                  <Box>
                    {rateKnown
                      ? <Typography>{t('amountToPay')} <strong>{computedUsdtAmount} USDT</strong></Typography>
                      : <Typography color="error">{t('courseUnavailable')}</Typography>}
                    <Typography color="text.secondary">{t('timer')} {remainingTime}</Typography>
                  </Box>
                </Box>
                {remainingMs === 0 && <Typography color="error" variant="body2">{t('rateExpired')}</Typography>}
                <Typography color="text.secondary" variant="body2">{t('linkValidFor30Min')}</Typography>
              </Box>
            )}

            {showAttempts && attempts.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>{t('previousPaymentAttempts')}</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('labelTime')}</TableCell>
                      <TableCell>{t('labelMethod')}</TableCell>
                      <TableCell>{t('labelAmount')}</TableCell>
                      <TableCell>{t('labelStatus')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attempts.map((a, i) => (
                      <TableRow key={i}>
                        <TableCell>{a.time}</TableCell>
                        <TableCell>{a.method}</TableCell>
                        <TableCell>{a.amount}</TableCell>
                        <TableCell><Chip size="small" label={a.status} color={getStatusColor(a.rawStatus)} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {paymentMethod && billData && (
          <Button variant="contained" color="primary" disabled={creating} onClick={handleConfirmPayment}>
            {t('confirmPayment')}
          </Button>
        )}
        <Button onClick={onClose} variant="outlined">{t('labelClose')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDialog;
